import { ATTRIBUTE_TYPES } from 'Constants/attributes.constants'
import ModalComp from 'Components/common/Modal/Modal'
import TextAreaAttribute from 'Components/attributes/TextAreaAttribute/TextArea'
import RadioButtonAttribute from 'Components/attributes/RadioButtonAttribute/RadioButton'
import styles from './SubmitModal.module.less'
import Button from 'antd/lib/button'
import Tooltip from 'antd/lib/tooltip'
import { useCallback, useMemo, useReducer, useState } from 'react'
import { isEmpty } from 'lodash'
import {
    AttributeModalType,
    ErrorAndWarningCount,
    TaskAdditionalInfo,
    TaskMode,
} from 'Types/Tasks.types'
import Checkbox from 'antd/lib/checkbox'
import { sendAnalyticsTrack } from 'Utils/analytics.utils'
import { submitModalReducer } from 'Hooks/use-submit-modal'
import { useCSATFeedback } from 'Hooks/use-csat-feedback'
import { useMessage } from 'shared/hooks/use-message'
import type { CSATFeedbackData } from 'Types/CSAT.types'
import { Studio } from 'Core/Studio'

export type SubmitModalProps = {
    isRejected: boolean
    isAIUsageConfirmationEnabled: boolean
    taskAdditionalInfo: TaskAdditionalInfo
    errorAndWarningCount: ErrorAndWarningCount
    timeElapsedInSeconds: () => number
    studio: Studio
    onConfirm: (payload: {
        comment: string
        confidenceLevel?: string
        feedbackData?: CSATFeedbackData
    }) => void
    onCancel: () => void
    getVisibility: () => boolean
    getMetaData: () => { taskMode?: TaskMode; isAccept?: boolean } | undefined
}

export const SubmitModal: React.FC<SubmitModalProps> = props => {
    const [messageApi] = useMessage()
    const [isAIConfirmationChecked, setIsAIConfirmationChecked] = useState(false)
    const taskFeedbackConfig = props.studio?.taskConfig?.assignment?.task?.config?.feedback_config
    const feedbackConfig = taskFeedbackConfig?.type === 'CSAT' ? taskFeedbackConfig : undefined
    const { csatSurveyUI, resetCSATFeedback, getFeedbackData } = useCSATFeedback(feedbackConfig)

    const [submitProps, dispatchSubmitModal] = useReducer(submitModalReducer, {
        confidenceLevel: '',
        comment: '',
    })

    const mode: 'accept' | 'submit' = props.getMetaData()?.isAccept ? 'accept' : 'submit'
    const modalHeader = props.getMetaData()?.isAccept ? 'Accept task?' : 'Submit task'

    const handleChange = useCallback((val: string | undefined, type?: AttributeModalType) => {
        dispatchSubmitModal({ type, payload: val })
    }, [])

    const commonAnalyticsObject = useMemo(() => {
        return {
            project_id: props.taskAdditionalInfo.projectId,
            task_id: props.taskAdditionalInfo.taskId,
            user_id: props.taskAdditionalInfo.assignedUserId,
            step_id: props.taskAdditionalInfo.stepId,
        }
    }, [
        props.taskAdditionalInfo.projectId,
        props.taskAdditionalInfo.taskId,
        props.taskAdditionalInfo.assignedUserId,
        props.taskAdditionalInfo.stepId,
    ])

    const isDisabled = useMemo(() => {
        if (props.isAIUsageConfirmationEnabled && !isAIConfirmationChecked) {
            return true
        }

        if (props.getMetaData()?.taskMode !== TaskMode.Maker) {
            return false
        }

        if (props.isRejected) {
            return isEmpty(submitProps.confidenceLevel) || isEmpty(submitProps.comment?.trim())
        } else {
            return isEmpty(submitProps.confidenceLevel)
        }
    }, [isAIConfirmationChecked, props, submitProps.comment, submitProps.confidenceLevel])

    const getSubmitModalMessage = useMemo(() => {
        switch (props.getMetaData()?.taskMode) {
            case TaskMode.Maker:
            case TaskMode.Rework:
                return 'Are you confident about your response?'
            case TaskMode.Triager:
                return 'Is the response of best quality?'
            default:
                return 'Add any additional comments below'
        }
    }, [props])

    const confidenceRadioOptions = useMemo(() => {
        if (
            ![TaskMode.Maker, TaskMode.Triager].includes(props.getMetaData()?.taskMode as TaskMode)
        ) {
            return null
        }
        const radioOptions = (() => {
            if (props.getMetaData()?.taskMode === TaskMode.Maker) {
                return [
                    { label: 'Yes, I’m confident', value: 'CONFIDENT' },
                    { label: 'No, My response needs a review', value: 'NEEDS_REVIEW' },
                ]
            } else if (props.getMetaData()?.taskMode === TaskMode.Triager) {
                return [
                    { label: 'Yes', value: 'Best Quality Task' },
                    { label: 'No, needs some editing', value: 'Needs some editing' },
                ]
            }
            return []
        })()

        return (
            <RadioButtonAttribute
                className={styles['submit-modal-radio-btn']}
                value={submitProps.confidenceLevel}
                onChange={e => {
                    handleChange(e, AttributeModalType.Radio)
                }}
                id={'radio'}
                width="100%"
                type={ATTRIBUTE_TYPES.RADIO}
                options={radioOptions}
            />
        )
    }, [props, submitProps.confidenceLevel, handleChange])

    const aiConfirmationCheckbox = useMemo(() => {
        if (props.isAIUsageConfirmationEnabled) {
            return (
                <div className={styles['modal-ai-confirmation']}>
                    <Checkbox
                        value={isAIConfirmationChecked}
                        onChange={() => setIsAIConfirmationChecked(val => !val)}
                        data-testid="ai-confirmation-checkbox"
                    >
                        I hereby confirm that no AI-generated text was used in this submission.
                    </Checkbox>
                </div>
            )
        }
        return null
    }, [isAIConfirmationChecked, props.isAIUsageConfirmationEnabled])

    const handleSubmit = useCallback(() => {
        const payload: {
            comment: string
            confidenceLevel?: string
            feedbackData?: CSATFeedbackData
        } = {
            comment: submitProps.comment ?? '',
        }

        if (!props.getMetaData()?.isAccept) {
            payload.confidenceLevel =
                props.getMetaData()?.taskMode === TaskMode.Maker
                    ? submitProps.confidenceLevel ?? ''
                    : 'CONFIDENT'
        }

        const { feedbackData, error } = getFeedbackData()
        if (error) {
            messageApi.error(error)
            return
        }
        payload.feedbackData = feedbackData

        props.onConfirm(payload)
        sendAnalyticsTrack('ConfirmSubmitCTA_Clicked', {
            ...commonAnalyticsObject,
            confident_to_submit: submitProps.confidenceLevel === 'CONFIDENT',
            is_review_needed: submitProps.confidenceLevel === 'NEEDS_REVIEW',
            error_count: props.errorAndWarningCount.errorCount,
            warning_count: props.errorAndWarningCount.warningCount,
            duration_since_task_start: Math.floor(props.timeElapsedInSeconds() / 1000),
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.getMetaData,
        props.errorAndWarningCount,
        submitProps.comment,
        submitProps.confidenceLevel,
        commonAnalyticsObject,
        getFeedbackData,
        messageApi,
    ])

    const handleCancel = useCallback(() => {
        props.onCancel()
        dispatchSubmitModal({ type: AttributeModalType.Radio, payload: '' })
        dispatchSubmitModal({ type: AttributeModalType.TextArea, payload: '' })
        resetCSATFeedback()

        sendAnalyticsTrack('CancelSubmitCTA_Clicked', {
            ...commonAnalyticsObject,
        })
    }, [props, commonAnalyticsObject, dispatchSubmitModal, resetCSATFeedback])

    const tooltipDisabledReason = useMemo(() => {
        if (props.getMetaData()?.taskMode !== TaskMode.Maker || mode !== 'submit') {
            return ''
        }

        if (isEmpty(submitProps.confidenceLevel)) {
            return 'Please select the confidence level.'
        }

        if (props.isRejected && isEmpty(submitProps.comment?.trim())) {
            return 'Please provide your comments.'
        }

        return ''
    }, [mode, props, submitProps.comment, submitProps.confidenceLevel])

    return (
        <ModalComp
            data-testid="submit-modal-wrapper"
            visible={props.getVisibility()}
            centered
            className={styles.submitModalRoot}
            modalHeader={<div className={'modal-header'}>{modalHeader}</div>}
            modalFooter={
                <div className={'modal-button'}>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Tooltip title={tooltipDisabledReason}>
                        <Button type="primary" onClick={handleSubmit} disabled={isDisabled}>
                            Confirm
                        </Button>
                    </Tooltip>
                </div>
            }
            onCancel={handleCancel}
        >
            <div className={styles['submit-modal']} data-testid="submit-modal-wrapper">
                <div className={styles['submit-modal-title-w-radio-btn']}>
                    <span>{getSubmitModalMessage}</span>
                    {confidenceRadioOptions}
                </div>
                <div className={'modal-textarea-parent'}>
                    <TextAreaAttribute
                        className={'modal-textarea'}
                        value={submitProps.comment}
                        onChange={e => {
                            handleChange(e, AttributeModalType.TextArea)
                        }}
                        placeholder="Additional comments..."
                        id={'modal'}
                        rows={6}
                        width="100%"
                        type={ATTRIBUTE_TYPES.TEXTAREA}
                    />
                </div>
                {aiConfirmationCheckbox}

                {csatSurveyUI}
            </div>
        </ModalComp>
    )
}
